// @ts-nocheck
import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TextField, Box, Checkbox, Typography } from '@mui/material';

import { CHECKLIST_TYPES } from '../../../../const/CommonConst';
import { isValueEmpty } from '../../../../utils/Utils';
import RenderCheckListAttribute from '../ChecklistAttribute/RenderCheckListAttribute';
import useModelInventory from '../../../../hooks/ModelInventory/useModelInventory';

/**
 * Checklist field component that handles both normal and savedSearch types
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {Object} props.value - Current field value with structure { type, checkedEntity, ... }
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - The attribute object with metadata
 */
const ChecklistFieldComponent = ({
  label,
  value,
  onChange,
  attribute,
  titleLabel,
  entityId = null,
  entityType = null,
}) => {
  const [textValue, setTextValue] = useState('');
  const { getAllModelEntity } = useModelInventory();

  // Initialize textValue for normal checklist type
  useEffect(() => {
    if (attribute?.checklist_type === CHECKLIST_TYPES.NORMAL) {
      if (typeof value?.checkedEntity === 'string') {
        setTextValue(value.checkedEntity);
      } else if (
        typeof value?.checkedEntity === 'object' &&
        value?.checkedEntity &&
        Object.keys(value.checkedEntity).length > 0
      ) {
        // If we have checkboxes, clear the text field
        setTextValue('');
      } else {
        setTextValue('');
      }
    }
  }, [value, attribute?.checklist_type]);

  /**
   * Handles text input change for normal checklist type
   * @function
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
   * @returns {void}
   */
  const handleTextChange = useCallback(
    (e) => {
      setTextValue(e.target.value);
    },
    [setTextValue]
  );

  /**
   * Handles text input blur for normal checklist type
   * Creates checklist from comma-separated values
   * @function
   * @returns {void}
   */
  const handleTextBlur = useCallback(() => {
    if (!isValueEmpty(textValue)) {
      // Get existing items or create new object
      const existingItems =
        value?.checkedEntity && typeof value.checkedEntity === 'object'
          ? { ...value.checkedEntity }
          : {};

      // Add new items from text input
      textValue
        .trim()
        .split(',')
        .forEach((option) => {
          const trimmedOption = option.trim();
          if (
            !isValueEmpty(trimmedOption) &&
            !(trimmedOption in existingItems)
          ) {
            existingItems[trimmedOption] = false; // Initially unchecked
          }
        });

      onChange({
        type: 'normal',
        checkedEntity: existingItems,
      });

      // Clear the text field after processing
      setTextValue('');
    }
  }, [textValue, onChange, value, isValueEmpty, setTextValue]);

  /**
   * Handles key press events for text input
   * Creates checklist when Enter key is pressed
   * @function
   * @param {React.KeyboardEvent<HTMLInputElement>} e - The keyboard event
   * @returns {void}
   */
  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault(); // Prevent form submission
        handleTextBlur(); // Use the same logic as blur
      }
    },
    [handleTextBlur]
  );

  /**
   * Handles checkbox change for a specific item
   * @function
   * @param {string} item - The checklist item
   * @param {boolean} currentValue - Current checked state
   * @returns {Function} Event handler function
   */
  const createCheckboxHandler = useCallback(
    (item, currentValue) => () => {
      const updatedCheckedEntity = {
        ...value?.checkedEntity,
        [item]: !currentValue,
      };

      onChange({
        ...value,
        type: 'normal',
        checkedEntity: updatedCheckedEntity,
      });
    },
    [value, onChange]
  );

  /**
   * Renders individual checkbox for normal checklist items
   * @function
   * @param {string} item - The checklist item
   * @param {boolean} checked - Whether the item is checked
   * @returns {React.ReactElement} The checkbox component
   */
  const renderCheckboxItem = useCallback(
    (item, checked) => {
      return (
        <Box
          key={item}
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Checkbox
            checked={checked}
            onChange={createCheckboxHandler(item, checked)}
            sx={{ padding: 0, mr: 1 }}
            size="small"
          />
          <Typography variant="body2">{item}</Typography>
        </Box>
      );
    },
    [createCheckboxHandler]
  );

  /**
   * Mock formik form object for RenderCheckListAttribute compatibility
   */
  const mockFormikForm = {
    values: {
      [attribute?.id]: value,
    },
    setFieldValue: useCallback(
      (fieldPath, fieldValue) => {
        onChange(fieldValue);
      },
      [onChange]
    ),
  };

  // Render based on checklist type
  if (attribute?.checklist_type === CHECKLIST_TYPES.SAVE_SEARCH) {
    // For savedSearch type, use the existing RenderCheckListAttribute component
    return (
      <Box>
        <RenderCheckListAttribute
          attribute={attribute}
          formikForm={mockFormikForm}
          mode="edit"
          getAllModelEntity={getAllModelEntity}
          entityId={entityId}
          entity_type={entityType}
        />
      </Box>
    );
  }

  // For normal type, render text field and checkboxes
  const hasChecklistItems =
    value?.checkedEntity &&
    typeof value.checkedEntity === 'object' &&
    Object.keys(value.checkedEntity).length > 0;

  return (
    <Box>
      {/* Text field for entering comma-separated values */}
      {!hasChecklistItems && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <TextField
            label={label}
            title={titleLabel}
            value={textValue}
            placeholder="Enter comma-separated values"
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            onKeyDown={handleKeyPress}
            spellCheck={true}
            type="text"
            rows={2}
            fullWidth
          />
          <Typography variant="body2">
            <Typography variant="body1">Note:</Typography> Enter values
            separated by commas. Press Enter or click outside to create
            checklist. You cannot add more items after creating the checklist.
          </Typography>
        </Box>
      )}

      {/* Render checkboxes when items exist */}
      {hasChecklistItems && (
        <Box>
          {Object.entries(value.checkedEntity).map(([item, checked]) =>
            renderCheckboxItem(item, checked)
          )}
        </Box>
      )}
    </Box>
  );
};

ChecklistFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.shape({
    type: PropTypes.string,
    checkedEntity: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array,
      PropTypes.object,
    ]),
    entityType: PropTypes.string,
    filterName: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    checklist_type: PropTypes.string,
    section_name: PropTypes.string,
  }).isRequired,
  entityId: PropTypes.string,
  entityType: PropTypes.string,
};

export default ChecklistFieldComponent;
